import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { MotifDivider, Motif } from "../components/Motif";
import { MapPin, Clock, Phone } from "lucide-react";
import { useContent } from "../lib/content";

const CAT_IMAGES = {
  statues: "https://images.pexels.com/photos/264843/pexels-photo-264843.jpeg",
  rosary: "https://images.pexels.com/photos/5332851/pexels-photo-5332851.jpeg",
  crucifix: "https://images.pexels.com/photos/3775124/pexels-photo-3775124.jpeg",
  candles: "https://images.pexels.com/photos/3775140/pexels-photo-3775140.jpeg",
  bibles: "https://images.unsplash.com/photo-1585923035139-4f1e0cb749f5",
  gifts: "https://images.pexels.com/photos/599268/pexels-photo-599268.jpeg",
};

export default function Home() {
  const { content: c } = useContent();
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => {
      setAllProducts(r.data);
      setProducts(r.data.slice(0, 8));
    });
    api.get("/categories").then((r) => setCategories(r.data.slice(0, 5)));
  }, []);

  const newArrivals = allProducts.filter(
    (p) => (p.badge || "").toLowerCase() === "new"
  );

  const RITUALS = [
    {
      eyebrow: c.ritual_card_1_eyebrow,
      title: c.ritual_card_1_title,
      text: c.ritual_card_1_text,
      image: c.ritual_card_1_image,
      to: "/guides#communion",
    },
    {
      eyebrow: c.ritual_card_2_eyebrow,
      title: c.ritual_card_2_title,
      text: c.ritual_card_2_text,
      image: c.ritual_card_2_image,
      to: "/guides#baptism",
    },
    {
      eyebrow: c.ritual_card_3_eyebrow,
      title: c.ritual_card_3_title,
      text: c.ritual_card_3_text,
      image: c.ritual_card_3_image,
      to: "/guides#christmas",
    },
  ];

  const trust = [
    [c.trust_1_title, c.trust_1_subtitle],
    [c.trust_2_title, c.trust_2_subtitle],
    [c.trust_3_title, c.trust_3_subtitle],
    [c.trust_4_title, c.trust_4_subtitle],
  ];

  return (
    <div className="fade-up">
      {/* HERO */}
      <section className="relative min-h-[88vh] overflow-hidden bg-[var(--srishti-bg-alt)]" data-testid="hero-section">
        <div className="absolute inset-0 hero-plate-bg" aria-hidden="true">
          <img src={c.hero_image} alt="" className="hero-plate-img" />
        </div>
        <div className="absolute inset-0 hero-vignette" aria-hidden="true" />

        <div className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center px-6 md:px-12 lg:px-20 py-20 md:py-28">
          <div className="md:col-span-7">
            <span className="eyebrow text-[var(--srishti-saffron)]">{c.hero_eyebrow}</span>
            <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl text-[var(--srishti-text)] mt-4 leading-[0.95]">
              {c.hero_title_line1}<br />
              <em className="not-italic text-[var(--srishti-maroon)]">{c.hero_title_line2}</em>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[var(--srishti-text-secondary)] max-w-xl leading-relaxed">
              {c.hero_subtitle}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/shop" className="btn-maroon" data-testid="hero-shop-button">
                {c.hero_cta_primary}
              </Link>
              <Link to="/guides" className="btn-outline-gold" data-testid="hero-guides-button">
                {c.hero_cta_secondary}
              </Link>
            </div>
          </div>
          <div className="md:col-span-5 flex justify-center md:justify-end">
            <div className="hero-plate-frame">
              <img src={c.hero_image} alt="Devotional" className="hero-plate-foreground" data-testid="hero-plate-image" />
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28" data-testid="categories-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <span className="eyebrow">{c.categories_eyebrow}</span>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-2 text-[var(--srishti-text)]">
                {c.categories_title}
              </h2>
            </div>
            <Link to="/shop" className="text-sm uppercase tracking-widest font-semibold text-[var(--srishti-maroon)]">
              View all →
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 grid-rows-2 gap-3 md:gap-4 h-[520px] md:h-[640px]">
            {categories.map((cat, i) => {
              const span = i === 0 ? "col-span-2 row-span-2" : i === 3 ? "row-span-2" : "";
              return (
                <Link
                  key={cat.slug}
                  to={`/shop?category=${cat.slug}`}
                  className={`relative image-zoom group bg-[var(--srishti-bg-alt)] ${span}`}
                  data-testid={`category-tile-${cat.slug}`}
                >
                  <img src={CAT_IMAGES[cat.slug]} alt={cat.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <p className="font-serif-display text-2xl md:text-3xl text-[#FDFBF7] leading-tight">{cat.name}</p>
                    <p className="text-xs text-[#FDFBF7]/80 mt-1">{cat.tagline}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS — horizontal carousel */}
      {newArrivals.length > 0 && (
        <section
          className="px-6 md:px-12 lg:px-20 pt-20 md:pt-24 pb-4"
          data-testid="new-arrivals-section"
        >
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
              <div>
                <span className="eyebrow">{c.new_arrivals_eyebrow}</span>
                <h2 className="font-serif-display text-4xl md:text-5xl mt-2 leading-tight">
                  {c.new_arrivals_title}
                </h2>
              </div>
              <p className="text-sm text-[var(--srishti-text-secondary)] max-w-md">
                {c.new_arrivals_subtitle}
              </p>
            </div>
          </div>
          <div className="max-w-[1400px] mx-auto -mx-6 md:-mx-12 lg:-mx-20">
            <div
              className="srishti-carousel flex gap-5 md:gap-7 overflow-x-auto pb-6 px-6 md:px-12 lg:px-20 snap-x snap-mandatory scroll-smooth"
              data-testid="new-arrivals-carousel"
            >
              {newArrivals.map((p) => (
                <div
                  key={p.id}
                  className="snap-start shrink-0 w-[72%] sm:w-[46%] md:w-[30%] lg:w-[22%]"
                  data-testid={`new-arrival-${p.id}`}
                >
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="max-w-[600px] mx-auto px-6">
        <MotifDivider label="Featured" />
      </div>

      {/* FEATURED */}
      <section className="px-6 md:px-12 lg:px-20 pb-20" data-testid="featured-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <h2 className="font-serif-display text-4xl md:text-5xl">{c.featured_title}</h2>
            <p className="text-sm text-[var(--srishti-text-secondary)] max-w-md">{c.featured_subtitle}</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-7">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* RITUALS */}
      <section className="bg-[var(--srishti-bg-alt)] px-6 md:px-12 lg:px-20 py-20 md:py-28" data-testid="rituals-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end mb-12">
            <div className="md:col-span-5">
              <span className="eyebrow">{c.rituals_eyebrow}</span>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-2 leading-tight">
                {c.rituals_title_line1}<br />
                <em className="not-italic text-[var(--srishti-saffron)]">{c.rituals_title_line2}</em>
              </h2>
            </div>
            <p className="md:col-span-6 md:col-start-7 text-base text-[var(--srishti-text-secondary)] leading-relaxed">
              {c.rituals_subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {RITUALS.map((r, i) => (
              <Link
                key={i}
                to={r.to}
                className={`group block image-zoom relative ${
                  i === 0 ? "md:col-span-7 md:row-span-2 aspect-[4/5] md:aspect-auto md:h-[640px]" : "md:col-span-5 aspect-[5/4]"
                }`}
                data-testid={`ritual-card-${i}`}
              >
                <img src={r.image} alt={r.title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <span className="eyebrow text-[var(--srishti-gold)]">{r.eyebrow}</span>
                  <p className="font-serif-display text-3xl md:text-4xl text-[#FDFBF7] mt-2">{r.title}</p>
                  <p className="text-sm text-[#FDFBF7]/85 mt-2 max-w-md leading-relaxed">{r.text}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* VISIT */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28" data-testid="visit-section">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="image-zoom aspect-[5/6] md:aspect-[4/5] order-2 md:order-1 bg-[var(--srishti-bg-alt)] overflow-hidden">
            <img
              src={c.visit_image}
              alt="Boutique"
              className="w-full h-full object-cover"
              style={{ objectPosition: "center 60%" }}
              data-testid="visit-section-image"
            />
          </div>
          <div className="order-1 md:order-2">
            <span className="eyebrow">{c.visit_section_eyebrow}</span>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">{c.visit_section_title}</h2>
            <p className="mt-6 text-base text-[var(--srishti-text-secondary)] leading-relaxed max-w-md">
              {c.visit_section_paragraph}
            </p>

            <ul className="mt-8 space-y-4 text-sm">
              <li className="flex gap-3">
                <MapPin size={18} className="text-[var(--srishti-saffron)] mt-0.5 shrink-0" />
                <span>{c.store_address}</span>
              </li>
              <li className="flex gap-3">
                <Clock size={18} className="text-[var(--srishti-saffron)] mt-0.5 shrink-0" />
                <span>{c.store_hours}</span>
              </li>
              <li className="flex gap-3">
                <Phone size={18} className="text-[var(--srishti-saffron)] mt-0.5 shrink-0" />
                <span>{c.store_phone} · For pickup queries</span>
              </li>
            </ul>

            <div className="mt-9">
              <Link to="/visit" className="btn-outline-gold" data-testid="get-directions-button">
                {c.visit_section_cta}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="border-y border-[var(--srishti-border)] bg-[var(--srishti-bg-alt)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {trust.map(([t, s], i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <Motif className="text-[var(--srishti-gold)]" />
              <p className="font-serif-display text-xl text-[var(--srishti-text)]">{t}</p>
              <p className="text-xs uppercase tracking-widest text-[var(--srishti-text-secondary)]">{s}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
